/**
 * Collaboration channels.
 *
 * One per incident, created with it. The list exists so an analyst can find the conversation
 * about something they were not on shift for — the channel is the record of what was done, so
 * being able to read it afterwards is most of its value.
 */

import { Link } from 'react-router-dom'
import { api } from '../api'
import { useSession } from '../App'
import { Async, Card, Chip, Empty, Mono, fmtRelative, useAsync } from '../components/ui'

interface ChannelRow {
  id: string
  name: string
  topic: string
  kind: string
  incident_id?: string
  archived: boolean
  message_count: number
  summary: {
    actions: number
    failed_actions: number
    human_participants: string[]
    ai_participants: string[]
    last_at?: number
  }
}

export default function Channels() {
  const { me } = useSession()
  const channels = useAsync<ChannelRow[]>(() => api.get<ChannelRow[]>('/channels'), [me.tenant])

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Channels</h1>
          <div className="sub">
            Every incident gets exactly one channel, created with it, and every action taken on
            that incident is posted there — automated or not. The channel is the record.
          </div>
        </div>
      </div>

      <Async
        state={channels}
        empty={
          <Card>
            <Empty title="No channels yet">
              A channel appears when correlation raises an incident.
            </Empty>
          </Card>
        }
      >
        {(list) => (
          <div className="table-wrap card">
            <table>
              <thead>
                <tr>
                  <th>Channel</th>
                  <th>Topic</th>
                  <th>Messages</th>
                  <th>Actions</th>
                  <th>Participants</th>
                  <th>Last activity</th>
                </tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <Mono>{c.name}</Mono>
                      {c.archived && <Chip tone="neutral">archived</Chip>}
                    </td>
                    <td className="small">
                      {c.incident_id ? <Link to={`/incidents/${c.incident_id}`}>{c.topic}</Link> : c.topic}
                    </td>
                    <td className="tnum">{c.message_count}</td>
                    <td className="tnum">
                      {c.summary.actions}
                      {c.summary.failed_actions > 0 && (
                        <>
                          {' '}
                          <Chip tone="danger">{c.summary.failed_actions} failed</Chip>
                        </>
                      )}
                    </td>
                    <td className="small">
                      {c.summary.human_participants.map((p) => (
                        <Chip key={p} tone="outline">{p}</Chip>
                      ))}
                      {c.summary.ai_participants.map((p) => (
                        <Chip key={p} tone="neutral" title="An AI analyst">{p}</Chip>
                      ))}
                      {c.summary.human_participants.length === 0 && c.summary.ai_participants.length === 0 && (
                        <span className="faint">automated only</span>
                      )}
                    </td>
                    <td className="small muted nowrap">
                      {c.summary.last_at ? fmtRelative(c.summary.last_at) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Async>
    </>
  )
}
